import { createSelector } from 'reselect';
import Immutable from 'seamless-immutable';
import { MetadataConstants, MetadataSelectors, SessionSelectors, SelectHelper } from 'invision-core';
import { CurrentCustomerIdSelector, RouteParams, SelectedCustomerSelector } from './customer.selectors';
import { findIndex, path, pathOr, propEq } from 'ramda';
var getInteractionTypeDescription = function getInteractionTypeDescription(interactionTypeCodes, interaction) {
  var interactionType = interactionTypeCodes.find(function (interactionType) {
    return parseInt(interactionType.Value, 10) === interaction.Type;
  });
  return pathOr('', ['Description'], interactionType);
};
var getInteractionTypeName = function getInteractionTypeName(interactionTypeCodes, interaction) {
  var interactionType = interactionTypeCodes.find(function (interactionType) {
    return parseInt(interactionType.Value, 10) === interaction.Type;
  });
  return pathOr('', ['Name'], interactionType);
};
var InteractionTypeCodes = function InteractionTypeCodes(state) {
  return MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.InteractionType, state);
};
export var CurrentInteractionIdSelector = createSelector([RouteParams], function (params) {
  return params.interactionId;
});
var InteractionsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.interactions;
});
var DEFAULT_INTERACTION = Immutable({});
var CurrentInteractionSelector = createSelector([InteractionsSelector], function (interactionState) {
  return interactionState.currentInteraction ? interactionState.currentInteraction : DEFAULT_INTERACTION;
});
export var FormattedInteractionSelector = createSelector([CurrentInteractionSelector, InteractionTypeCodes], function (interaction, interactionTypeCodes) {
  return interaction.set('TypeDescription', getInteractionTypeDescription(interactionTypeCodes, interaction));
});
export var InteractionErrorSelector = createSelector([InteractionsSelector], function (interactionState) {
  return interactionState.interactionError;
});
var RecoverableInteractionsSelector = function RecoverableInteractionsSelector(state) {
  return state.customercare.recoverableUiState.interactions;
};
var DEFAULT_INTERACTION_MAP = [];
export var InteractionsListSelector = createSelector([InteractionsSelector], function (interactionState) {
  return interactionState.data.records ? interactionState.data.records : DEFAULT_INTERACTION_MAP;
});
var DEFAULT_FORMATTED_INTERACTIONS = [];
export var FormattedInteractionsSelector = createSelector([InteractionsListSelector, InteractionTypeCodes], function (interactions, interactionTypeCodes) {
  if (interactions.length === 0 || interactionTypeCodes.length === 0) {
    return DEFAULT_FORMATTED_INTERACTIONS;
  }
  return interactions.map(function (interaction) {
    return interaction.set('TypeDescription', getInteractionTypeDescription(interactionTypeCodes, interaction));
  });
});
export var FormattedInteractionsWithNamesSelector = createSelector([InteractionsListSelector, InteractionTypeCodes], function (interactions, interactionTypeCodes) {
  if (interactions.length === 0 || interactionTypeCodes.length === 0) {
    return DEFAULT_FORMATTED_INTERACTIONS;
  }
  return interactions.map(function (interaction) {
    return interaction.set('TypeName', getInteractionTypeName(interactionTypeCodes, interaction));
  });
});
export var InteractionsLoadedSelector = createSelector([InteractionsListSelector], function (interactions) {
  return interactions.length > 0;
});
export var InteractionsLoadingSelector = createSelector([InteractionsSelector], function (interactionState) {
  return interactionState.isFetchingInteractions;
});
var InteractionFilterData = createSelector([RecoverableInteractionsSelector], function (recoverableInteraction) {
  return recoverableInteraction.filterData;
});
export var CategoryTypeSelector = createSelector([InteractionFilterData], function (filterData) {
  return filterData.categoryType;
});
export var InteractionTypeSelector = createSelector([InteractionFilterData], function (filterData) {
  return filterData.interactionType;
});
export var SelectedPageSizePreference = createSelector([InteractionFilterData], function (filterData) {
  return filterData.pageSizePreference;
});
export var ShowChangesSelector = createSelector([InteractionFilterData], function (filterData) {
  return filterData.onlyShowChangedProperties;
});
export var SortCriteriaSelector = createSelector([InteractionFilterData], function (filterData) {
  return pathOr(null, ['sortCriteria'], filterData);
});
export var PageNumberSelector = createSelector([RecoverableInteractionsSelector], function (interaction) {
  return pathOr(0, ['pageNumber'], interaction);
});
export var PageCountSelector = createSelector([InteractionsSelector], function (interactionState) {
  return pathOr(0, ['data', 'pageCount'], interactionState);
});
export var RecordCountSelector = createSelector([InteractionsSelector], function (interactionState) {
  return pathOr(0, ['data', 'recordCount'], interactionState);
});
export var EndRecordSelector = createSelector([PageNumberSelector, SessionSelectors.PageSizePreferenceSelector, RecordCountSelector], function (pageNumber, pageSize, recordCount) {
  var endNumber = pageNumber * pageSize;
  if (endNumber < recordCount) {
    return endNumber;
  }
  return recordCount;
});
export var StartRecordSelector = createSelector([PageNumberSelector, SessionSelectors.PageSizePreferenceSelector, RecordCountSelector], function (pageNumber, pageSize, recordCount) {
  if (recordCount <= 0) {
    return 0;
  }
  return (pageNumber - 1) * pageSize + 1;
});
export var InteractionTypeCodesFormatted = createSelector([CategoryTypeSelector, InteractionTypeCodes, InteractionTypeSelector], function (categoryType, interactionTypeCodes, interactionType) {
  var formattedInteractionCodes = [];
  interactionTypeCodes.forEach(function (code) {
    var categoryAttr = code.AdditionalProperties.find(function (prop) {
      return prop.Key === 'interaction_category_code';
    });
    if (categoryAttr && categoryType && categoryAttr.Value !== categoryType.value) {
      return;
    }
    var formattedCode = SelectHelper.codeValueToSelectOption(code);
    var selectedValue = interactionType ? interactionType.value : null;
    formattedCode.selected = formattedCode.value === selectedValue;
    formattedInteractionCodes.push(formattedCode);
  });
  return formattedInteractionCodes;
});
export var InteractionPositionSelector = createSelector([StartRecordSelector, InteractionsListSelector, CurrentInteractionSelector], function (startingRecord, interactions, current) {
  if (!path(['Identifier'], current)) {
    return '';
  }
  var index = findIndex(propEq(current.Identifier, 'Identifier'), interactions);
  return index + startingRecord;
});
export var NextInteractionIdSelector = createSelector([CurrentInteractionSelector, InteractionsListSelector], function (current, interactions) {
  if (!path(['Identifier'], current)) {
    return null;
  }
  var index = findIndex(propEq(current.Identifier, 'Identifier'), interactions);
  return pathOr(null, [index + 1, 'Identifier'], interactions);
});
export var PreviousInteractionIdSelector = createSelector([CurrentInteractionSelector, InteractionsListSelector], function (current, interactions) {
  if (!path(['Identifier'], current)) {
    return null;
  }
  var index = findIndex(propEq(current.Identifier, 'Identifier'), interactions) - 1;
  if (index < -1) {
    index = interactions.length - 1;
  }
  return interactions[index] ? interactions[index].Identifier : null;
});
export var FilterDataSelector = createSelector([CategoryTypeSelector, CurrentCustomerIdSelector, InteractionTypeSelector, PageNumberSelector, SessionSelectors.PageSizePreferenceSelector], function (categoryType, currentCustomerId, interactionType, currentPage, pageSize) {
  return {
    customerId: currentCustomerId,
    InteractionCategory: pathOr(null, ['id'], categoryType),
    InteractionType: pathOr(null, ['id'], interactionType),
    PageNumber: currentPage,
    PageSize: pageSize,
    SearchBySubscriber: true
  };
});